import { FLANGE, SCULPT } from "@/config/designer";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { isCuttingTool, useDesignerStore, type ToolId } from "@/state/designerStore";
import {
  AngleIcon,
  CarveIcon,
  CutIcon,
  FixingIcon,
  FlangeIcon,
  SmoothIcon,
  TextureIcon,
  TrashIcon,
} from "./Icons";

interface ToolPanelProps {
  engine: DesignerEngine;
  /** Set by the viewport; needs the live camera to build the sweep. */
  cutRunner: React.MutableRefObject<(() => void) | null>;
}

const TOOLS: ReadonlyArray<{
  id: ToolId;
  name: string;
  hint: string;
  icon: React.ReactElement;
}> = [
  { id: "carve", name: "Carve", hint: "Drag to remove foam · 1", icon: <CarveIcon /> },
  { id: "smooth", name: "Smooth", hint: "Soften a shape · 2 · hold Shift", icon: <SmoothIcon /> },
  { id: "fixings", name: "Fixings", hint: "Countersunk screw holes · 3", icon: <FixingIcon /> },
  { id: "cut", name: "Cut", hint: "Saw straight through · 4", icon: <CutIcon /> },
  { id: "angle", name: "Angle", hint: "Snap an edge every 5° · 5", icon: <AngleIcon /> },
  { id: "flange", name: "Flange", hint: "Add a ramped floor edge · 6", icon: <FlangeIcon /> },
  { id: "texture", name: "Gloss paint", hint: "Paint smooth finish · 7", icon: <TextureIcon /> },
];

export function ToolPanel({ engine, cutRunner }: ToolPanelProps): React.ReactElement {
  const tool = useDesignerStore((s) => s.tool);
  const setTool = useDesignerStore((s) => s.setTool);
  const brushRadius = useDesignerStore((s) => s.brushRadius);
  const setBrushRadius = useDesignerStore((s) => s.setBrushRadius);
  const carveStrength = useDesignerStore((s) => s.carveStrength);
  const setCarveStrength = useDesignerStore((s) => s.setCarveStrength);
  const smoothStrength = useDesignerStore((s) => s.smoothStrength);
  const setSmoothStrength = useDesignerStore((s) => s.setSmoothStrength);
  const selectedFixingId = useDesignerStore((s) => s.selectedFixingId);
  const selectFixing = useDesignerStore((s) => s.selectFixing);
  const revision = useDesignerStore((s) => s.revision);
  const cutPoints = useDesignerStore((s) => s.cutPoints);
  const clearCutPoints = useDesignerStore((s) => s.clearCutPoints);
  const cutting = useDesignerStore((s) => s.cutting);
  const cutAngleDegrees = useDesignerStore((s) => s.cutAngleDegrees);
  const flangeWidth = useDesignerStore((s) => s.flangeWidth);
  const notify = useDesignerStore((s) => s.notify);
  const setShowOverallSmooth = useDesignerStore((s) => s.setShowOverallSmooth);
  const setShowFlangePreview = useDesignerStore((s) => s.setShowFlangePreview);

  // `revision` is the engine's change signal; reading it is what re-renders
  // this panel when fixings are added or removed.
  void revision;
  const fixingCount = engine.fixings.length;
  const glossCount = engine.glossDabs.length;
  const maxFlangeWidth = Math.min(
    FLANGE.maxWidth,
    Math.min(engine.preset.width, engine.preset.depth) * 0.4,
  );
  const effectiveFlangeWidth = Math.min(flangeWidth, maxFlangeWidth);
  const activeMaxStrength = tool === "smooth" ? SCULPT.maxSmoothStrength : SCULPT.maxStrength;
  const activeStrength = tool === "smooth" ? smoothStrength : carveStrength;
  const activeStrengthPercent = Math.round((activeStrength / activeMaxStrength) * 100);

  return (
    <div className="tool-dock">
      <div className="panel tools" role="group" aria-label="Tools">
        {TOOLS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="tool"
            aria-pressed={tool === item.id}
            aria-label={`${item.name}: ${item.hint}`}
            onClick={() => setTool(item.id)}
            data-tooltip={`${item.name} · ${item.hint}`}
          >
            {item.icon}
          </button>
        ))}
      </div>

      {isCuttingTool(tool) ? (
        <div className="panel fixing-help">
          {tool === "angle" ? (
            <span>
              <strong>Drag an edge guide</strong> across the hold. It snaps to the nearest 5° so
              30° and 45° edges are quick and exact.
            </span>
          ) : (
            <span>
              <strong>Drag across the hold</strong> to draw your cut line, then press Cut. The line
              is extended past both ends, so the saw always goes right through.
            </span>
          )}
          <span>
            {tool === "angle" && cutAngleDegrees !== null
              ? `Snapped angle: ${cutAngleDegrees}°. `
              : ""}
            The piece still joined to the flat back is kept; anything left loose comes away.
          </span>
          <div className="fixing-help__actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={cutPoints.length < 2 || cutting}
              onClick={() => cutRunner.current?.()}
            >
              {tool === "angle" ? <AngleIcon size={16} /> : <CutIcon size={16} />}
              {cutting
                ? "Cutting…"
                : tool === "angle" && cutAngleDegrees !== null
                  ? `Cut at ${cutAngleDegrees}°`
                  : "Cut"}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={cutPoints.length === 0 || cutting}
              onClick={clearCutPoints}
            >
              Clear
            </button>
          </div>
        </div>
      ) : tool === "flange" ? (
        <div className="panel controls controls--flange">
          <p className="control__flange-copy">
            Preview a flange that fills smoothly into the hold and finishes on a perfectly flat
            2 mm floor edge. It can grow beyond the untouched starting blank.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              engine.beginFlangePreview();
              setShowFlangePreview(true);
            }}
          >
            <FlangeIcon size={16} />
            Preview flange · {Math.round(effectiveFlangeWidth)} mm
          </button>
        </div>
      ) : tool === "texture" ? (
        <div className="panel controls controls--texture">
          <div>
            <div className="control__head">
              <span className="control__name">Paint size</span>
              <span className="control__value">{Math.round(brushRadius * 2)} mm</span>
            </div>
            <input
              type="range"
              min={SCULPT.minRadius}
              max={Math.min(SCULPT.maxRadius, Math.max(engine.preset.width, engine.preset.depth) * 0.6)}
              step={0.5}
              value={brushRadius}
              onChange={(event) => setBrushRadius(Number(event.target.value))}
              aria-label="Gloss paint size in millimetres"
            />
          </div>
          <p className="control__flange-copy">
            Every unpainted surface exports with fine sand-grip relief. Paint areas that should
            stay geometrically smooth and gloss-ready. {glossCount} paint dabs placed.
          </p>
          <button
            type="button"
            className="btn btn--ghost btn--danger"
            disabled={glossCount === 0}
            onClick={() => {
              engine.clearGlossPaint();
              notify("Gloss paint cleared. The full piece will export with fine sand grip.");
            }}
          >
            <TrashIcon size={16} />
            Clear gloss paint
          </button>
        </div>
      ) : tool === "fixings" ? (
        <div className="panel fixing-help">
          <span>
            <strong>Click the hold</strong> to add a hole for a 5 mm countersunk wood screw. It
            follows the surface angle and blends a smooth seat into the foam.
          </span>
          <span>
            Drag a hole to move it. {fixingCount} placed.
          </span>
          <div className="fixing-help__actions">
            <button
              type="button"
              className="btn btn--ghost btn--danger"
              disabled={!selectedFixingId}
              onClick={() => {
                if (!selectedFixingId) return;
                engine.removeFixing(selectedFixingId);
                selectFixing(null);
              }}
            >
              <TrashIcon size={16} />
              Delete
            </button>
          </div>
        </div>
      ) : (
        <div className="panel controls">
          <div>
            <div className="control__head">
              <span className="control__name">Size</span>
              <span className="control__value">{Math.round(brushRadius * 2)} mm</span>
            </div>
            <input
              type="range"
              min={SCULPT.minRadius}
              max={Math.min(SCULPT.maxRadius, Math.max(engine.preset.width, engine.preset.depth) * 0.6)}
              step={0.5}
              value={brushRadius}
              onChange={(event) => setBrushRadius(Number(event.target.value))}
              aria-label="Brush size in millimetres"
            />
          </div>

          <div>
            <div className="control__head">
              <span className="control__name">Strength</span>
              <span className="control__value">{activeStrengthPercent}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={activeStrengthPercent}
              onChange={(event) =>
                tool === "smooth"
                  ? setSmoothStrength((Number(event.target.value) / 100) * SCULPT.maxSmoothStrength)
                  : setCarveStrength((Number(event.target.value) / 100) * SCULPT.maxStrength)
              }
              aria-label="Brush strength"
            />
          </div>

          {tool === "smooth" ? (
            <div className="overall-smooth">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  engine.beginOverallSmoothPreview();
                  setShowOverallSmooth(true);
                }}
              >
                <SmoothIcon size={16} />
                Smooth entire piece
              </button>
            </div>
          ) : null}

          <p className="control__foot">
            <kbd>[</kbd> <kbd>]</kbd> resize · hold <kbd>Shift</kbd> to smooth · <kbd>MMB</kbd> orbit · <kbd>Shift</kbd>+<kbd>MMB</kbd> pan
          </p>
        </div>
      )}
    </div>
  );
}
